import type { BuildingIfcViewerSections } from "../../../types/buildings-sections/ifc-viewer";

export const ifcViewer: BuildingIfcViewerSections = {
  // Page shell
  loadingModels: "Loading 3D models…",
  loadModelsFailed: "Failed to load IFC models for this building.",
  noModelsTitle: "No 3D models yet",
  noModelsBodyPrefix: "Upload an",
  noModelsBodySuffix:
    "file from the Documents tab to populate this view. The pipeline will extract a 3D mesh and structured building attributes.",
  goToDocuments: "Go to Documents",

  // Canvas
  loadingModel: "Loading 3D model…",
  selectModelPrompt: "Select a model to view it in 3D.",
  noSpatialStructure: "This IFC has no spatial structure to display.",
  loadSceneFailed: "Failed to load 3D scene",
  noGeometryTitleSuffix: "has no 3D geometry",
  noGeometryDescription: "The model was skipped in the 3D view.",

  // Model picker
  selectModels: "Select models",
  browseAllModels: "Browse all {count} IFC files",
  sectionSelected: "Selected",
  sectionRecent: "Recent",
  sectionLatestUploads: "Latest uploads",
  browseModelsTitle: "Browse models",
  oneModelPrefix: "1 model:",
  modelsCountSuffix: "models",
  compareDifferences: "Compare differences",
  compareSelectTwoVersions: "Select exactly two versions to compare",
  applySelection: "Apply",
  elementsCountSuffix: "elements",
  loadToToggleVisibility: "Load this model to toggle visibility",
  hideInView: "Hide in 3D view",
  showInView: "Show in 3D view",
  showVersions: "Show older versions",
  hideVersions: "Hide older versions",
  versionPickerLabel: "Version",

  // Toolbar
  fitToModel: "Fit to model",
  downloadScreenshot: "Download screenshot",
  birdsEyeView: "Bird's-eye view",
  sideViewCycles: "Side view (cycles through the 4 sides)",
  sectionBoxLabel: "Section box (elevation & section cuts)",
  spatialStructure: "Spatial structure",
  modelIssuesTitle: "Issues attached to the model",
  showControls: "Show controls",
  hideControls: "Hide controls",

  // Discipline filter (WHA-3729)
  filtersLabel: "Filters",
  filtersTitle: "Filters",
  filtersEmpty: "No model loaded",
  filterNoOptions: "Nothing to filter",

  // Nav cluster
  moveForward: "Move forward",
  moveBack: "Move back",
  moveLeft: "Move left",
  moveRight: "Move right",
  zoomIn: "Zoom in",
  zoomOut: "Zoom out",
  scrollUp: "Scroll up",
  scrollDown: "Scroll down",
  showNavigation: "Show navigation",
  hideNavigation: "Hide navigation",
  dragRotateScrollZoom: "Drag to rotate · Scroll to zoom",

  // Spatial tree
  treeEmptyPrompt: "Load a model from the picker to see its spatial tree.",
  loadingTree: "Loading spatial tree…",
  loadTreeFailedPrefix: "Failed to load:",
  noSpatialData: "No spatial data.",
  loadingShort: "Loading…",
  loadElementsFailed: "Failed to load elements.",
  expand: "Expand",
  collapse: "Collapse",

  // Property panel
  propEmptyPrompt: "Click an element in the 3D view or the tree to see its properties.",
  loadingProperties: "Loading properties…",
  loadPropertiesFailed: "Failed to load properties.",
  elementWord: "Element",
  noElementData: "No data for this element.",
  fromModel: "From",
  layerWord: "layer",
  materialsHeader: "Materials",
  propType: "Type",
  propName: "Name",
  propLongName: "Long name",
  propGuid: "GUID",
  propElevation: "Elevation",
  unnamed: "(unnamed)",
  noProperties: "No properties for this element.",
  propertySingular: "property",
  propertyPlural: "properties",
  materialSingular: "material",
  materialPlural: "materials",
  yes: "Yes",
  no: "No",
  value: "Value",

  // Section panel
  sectionBoxTitle: "Section box",
  snapToFloors: "Snap to floors",
  snapToFloorsTooltip: "Snap the elevation cut to detected storey levels",
  resetBox: "Reset box",
  resetBoxTooltip: "Reset box to full model",
  clearSection: "Clear section",
  clearSectionTooltip: "Clear section — remove the cut",
  clearFilters: "Clear filters",
  hideSectionPanelTooltip: "Hide panel — the cut stays",
  elevationCut: "Elevation (height)",
  sectionWidth: "Section (width)",
  sectionDepth: "Section (depth)",
  topView: "Top view",
  sideView: "Side view",
  frontView: "Front view",
  close: "Close",
  minimize: "Minimise",
  suspendSection: "Suspend cut",
  suspendSectionTooltip: "Suspend the cut — see the whole model, keep the cut",
  resumeSection: "Resume cut",
  resumeSectionTooltip: "Resume the cut",
  showSectionHandles: "Show handles",
  hideSectionHandles: "Hide handles",
  isolateStorey: "Isolate storey",
  isolateStoreyTooltip: "Cut the box down to a single storey",
  wholeBuilding: "Whole building",
  customCut: "Custom",
  obliqueCuts: "Oblique cuts",
  obliqueCutFallbackLabel: "Cut",
  removeCut: "Remove cut",
  boundMinLabel: "{axis} — lower bound in metres",
  boundMaxLabel: "{axis} — upper bound in metres",

  // Context menu
  createIssue: "Create issue",
  takeScreenshot: "Take screenshot",
  highlightSimilar: "Highlight similar elements",
  cutHere: "Cut here",
  sectionBoxAroundThis: "Section box around this",

  // Issue panels
  propertiesTitle: "Properties",
  elementIssuesTitle: "Issues for this element",
  elementIssuesEmpty: "No issues linked to this element yet.",
  modelIssuesEmpty: "No issues are attached to this model yet.",
  issuesLoadFailed: "Failed to load issues for this model.",
  createIssueFromElement: "Create issue from this element",
  expandIssue: "Expand issue",
  goToElement: "Go to element",
  restoreViewpoint: "Restore saved view",
  viewpointUnsupported: "This saved view was made by a newer version of Franklin.",
  viewpointModelsGone: "The models this view was saved with are no longer available.",
  viewpointModelsPartial: "Some models from this saved view are no longer available.",
  viewpointElementGone:
    "The linked element is no longer in this model — the view was restored without it.",
  viewpointRestoreFailed: "Couldn't restore the saved view.",

  noData: "No data",

  // Issue → element links
  unlinkElementFailedTitle: "Couldn't unlink element",
  tryAgain: "Please try again.",
  unlinkElement: "Unlink element",
  openIn3dTitle: "Open in 3D viewer?",
  viewElementDescPrefix: "View ",
  viewElementDescSuffix: " in the 3D model viewer? The model will load with this element selected.",
  openViewer: "Open viewer",

  // Document-preview IFC confirm
  openBimModelQuestion: "Open this BIM model in the full 3D viewer?",
  noBuildingForModel:
    "This model has no building associated, so it can't be opened in the 3D viewer.",
  open3dModel: "Open 3D model",
  closeViewer: "Close viewer",
  previewLoading: "Loading preview…",
  previewUnavailable: "3D preview not available yet",

  // Version comparison / diff (WHA-3695)
  compareExit: "Exit comparison",
  compareSwap: "Swap older / newer",
  compareLoading: "Computing differences…",
  compareFailed: "Failed to compare these models.",
  compareChangesTitle: "Changes",
  compareMetricsTitle: "Metrics",
  compareNoChanges: "These two models are identical — no differences found.",
  compareSelectPrompt: "Select a changed element to see what changed.",
  changeAdded: "Added",
  changeRemoved: "Removed",
  changeModified: "Modified",
  changeUnchanged: "Unchanged",
  changedProperty: "property",
  changedMaterial: "material",
  changedAttribute: "attribute",
  diffBefore: "Before",
  diffAfter: "After",
  diffPropertiesHeader: "Properties & measurements",
  diffMaterialsHeader: "Materials",
  diffAttributesHeader: "Attributes",
  diffAttrStorey: "Storey",
  diffAttrParent: "Contained in",
  diffNoDetail: "No property or material differences recorded.",
  diffLoadingDetail: "Loading differences…",
  diffCloseDetail: "Close details",
  diffChangeFile: "Change file",
  diffSearchFiles: "Search files…",
  diffNoFiles: "No other IFC files",
  diffOlderVersions: "Older versions",

  // Diff UI overhaul (WHA-3695 follow-up)
  diffOverviewTitle: "Differences overview",
  openIn3dViewer: "Open in 3D viewer",
  compareSummaryTitle: "Summary",
  noOverlapInsight:
    "These two models share no elements — likely different disciplines or buildings, not two versions of one model.",
  groupByLabel: "Group by",
  groupByClass: "Class",
  groupByStorey: "Storey",
  groupByDiscipline: "Discipline",
  disciplineIncludes: "Includes",
  disciplineOtherHint: "Elements not attributed to a discipline.",
  diffSearchPlaceholder: "Search elements…",
  unassignedStorey: "No storey",
  isolateChanges: "Isolate changes",
  diffShowingOf: "showing {shown} of {total}",
  diffInThreeD: "{shown} of {total} shown in 3D",
  diffNotIn3d: "Not in 3D",
  diffGeometryNote: "{drawable} of {total} changes shown in 3D",
  diffGeometryNoteTooltip:
    "Whether a change appears in 3D is determined by the model's actual geometry. Openings and voids, MEP ports, and other bodyless elements carry no geometry, so they count as changes but can't be drawn in the 3D scene.",
  compareModelNewer: "Newer",
  compareModelOlder: "Older",
  renderFilterLabel: "Render filter",
  filterFloor: "Floor",
  filterDiscipline: "Discipline",
  filterClass: "Class",
  filterSpaces: "Show spaces",
  filterClear: "Clear",
  metricStoreys: "Storeys",
  metricSpaces: "Spaces",
  metricElements: "Elements",
  metricMaterials: "Materials",
  metricGrossArea: "Gross area (m²)",
  metricVolume: "Volume (m³)",
};
